import type { PartnerCockpitNotification } from '@houzy/schemas/partner-cockpit/notifications';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';

import { ListItemButtonLink } from '../../../components/ListItemButtonLink';
import { formatRelativeTime } from '../../../lib/time';
import { stateColor } from '../../../theme';
import { notificationLinkOptions } from '../utils/notificationLink';

const DESKTOP = 'md';
const PADDING_Y = 1.5;

export type NotificationItemProps = {
    notification: PartnerCockpitNotification;
    onSelect: (notification: PartnerCockpitNotification) => void;
    onMarkAsRead?: (notification: PartnerCockpitNotification) => void;
};

export function NotificationItem({ notification, onSelect, onMarkAsRead }: NotificationItemProps): ReactElement {
    const { t } = useTranslation();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | undefined>(undefined);

    const { titleKey, descriptionKey, params } = notification.content;
    const linkTarget = notificationLinkOptions(notification.action ?? undefined);
    const unread = !notification.read;
    const showMenu = onMarkAsRead !== undefined && unread;

    const content = (
        <Stack sx={{ minWidth: 0, width: '100%' }}>
            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                {t(titleKey, params)}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    pt: 0.25,
                    fontWeight: 'fontWeightRegular',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                }}>
                {t(descriptionKey, params)}
            </Typography>
            <Typography variant="overline" color="text.disabled" sx={{ pt: 0.5, textTransform: 'none' }}>
                {formatRelativeTime(notification.createdAt, t)}
            </Typography>
        </Stack>
    );

    const closeMenu = (): void => setMenuAnchor(undefined);

    const secondaryAction: ReactNode = (
        <Stack direction="row" spacing={0.5} alignItems="center">
            {unread && (
                <Box
                    role="img"
                    aria-label={t('NOTIFICATIONS.UNREAD')}
                    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }}
                />
            )}
            {showMenu && (
                <IconButton
                    size="small"
                    aria-label={t('NOTIFICATIONS.ITEM_ACTIONS')}
                    onClick={(event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            )}
        </Stack>
    );

    return (
        <ListItem
            divider
            disablePadding
            secondaryAction={secondaryAction}
            sx={{
                bgcolor: unread ? (theme) => stateColor(theme, 'hover') : 'transparent',
                // Against a three-line item the centred default lands beside the description, not the title.
                '& .MuiListItemSecondaryAction-root': {
                    top: (theme) => theme.spacing(PADDING_Y),
                    transform: 'none',
                },
            }}>
            {match(linkTarget)
                .with({ type: 'internal' }, (target) => (
                    <ListItemButtonLink
                        {...target.options}
                        onClick={() => onSelect(notification)}
                        sx={{ px: { xs: 2, [DESKTOP]: 2.5 }, py: PADDING_Y, alignItems: 'flex-start' }}>
                        {content}
                    </ListItemButtonLink>
                ))
                .with({ type: 'external' }, (target) => (
                    <ListItemButton
                        component="a"
                        href={target.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onSelect(notification)}
                        sx={{ px: { xs: 2, [DESKTOP]: 2.5 }, py: PADDING_Y, alignItems: 'flex-start' }}>
                        {content}
                    </ListItemButton>
                ))
                .with(P.nullish, () => (
                    <Box sx={{ px: { xs: 2, [DESKTOP]: 2.5 }, py: PADDING_Y, width: '100%' }}>{content}</Box>
                ))
                .exhaustive()}

            {showMenu && (
                <Menu anchorEl={menuAnchor} open={menuAnchor !== undefined} onClose={closeMenu}>
                    <MenuItem
                        onClick={() => {
                            onMarkAsRead(notification);
                            closeMenu();
                        }}>
                        {t('NOTIFICATIONS.MARK_AS_READ')}
                    </MenuItem>
                </Menu>
            )}
        </ListItem>
    );
}
